/**
 * AMP Cart Integration - Syncs theme cart icon bubble with Upsell by Amp cart updates
 *
 * When Upsell by Amp replaces the cart drawer, the theme's cart icon (#cart-icon-bubble)
 * doesn't update because Amp's cart operations bypass the theme's cart update flow.
 * This script listens for AMP_CART_UPDATED and AMP_CART_LOADED callbacks to refresh
 * the cart icon via Shopify's Section Rendering API.
 */
(function () {
  const CART_ICON_SELECTOR = '#cart-icon-bubble';
  const SECTION_ID = 'cart-icon-bubble';
  const LANDING_ADD_BUTTON_SELECTOR = '[id^="LandingPurchase-"] form[action$="/cart/add"] [type="submit"]';
  const AMP_CART_SELECTOR = '.amp-cart';
  const AMP_CART_OPEN_CLASS = 'amp-cart--open';
  const AMP_CART_CLOSE_SELECTOR = '.amp-cart__header-close-button, .amp-cart-drawer__overlay';
  const PENDING_ADD_TIMEOUT = 15000;
  let addTokenSequence = 0;
  let pendingAddToken = 0;
  let openingToken = 0;

  function wait(duration) {
    return new Promise((resolve) => window.setTimeout(resolve, duration));
  }

  function isAmpCartVisible(root) {
    if (!root || !root.classList.contains(AMP_CART_OPEN_CLASS)) return false;
    const style = getComputedStyle(root);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function markLandingAddPending(event) {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest(AMP_CART_CLOSE_SELECTOR)) {
      pendingAddToken = 0;
      return;
    }
    if (!event.target.closest(LANDING_ADD_BUTTON_SELECTOR)) return;

    const token = ++addTokenSequence;
    pendingAddToken = token;
    window.setTimeout(() => {
      if (pendingAddToken === token) pendingAddToken = 0;
    }, PENDING_ADD_TIMEOUT);
  }

  async function openAmpCartForPendingAdd() {
    const token = pendingAddToken;
    if (!token || openingToken === token) return;
    openingToken = token;

    try {
      // AMP opens the cart immediately after publishing its cart update. Give
      // that state change time to render before invoking the fallback API.
      await wait(500);

      for (let readinessAttempt = 0; readinessAttempt < 20; readinessAttempt += 1) {
        if (pendingAddToken !== token) return;

        const root = document.querySelector(AMP_CART_SELECTOR);
        if (isAmpCartVisible(root)) {
          pendingAddToken = 0;
          return;
        }

        const openCart = window.AMP_API && window.AMP_API.OPEN_CART;
        if (root && typeof openCart === 'function') {
          pendingAddToken = 0;
          try {
            await Promise.resolve(openCart());
          } catch (error) {
            console.warn('AMP cart integration: failed to open cart', error);
          }
          return;
        }

        await wait(250);
      }

      if (pendingAddToken === token) {
        pendingAddToken = 0;
      }
    } finally {
      if (openingToken === token) openingToken = 0;
    }
  }

  document.addEventListener('click', markLandingAddPending, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.querySelector(AMP_CART_SELECTOR)?.classList.contains(AMP_CART_OPEN_CLASS)) {
      pendingAddToken = 0;
    }
  }, true);

  function getSectionUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      (window.routes?.cart_url ? window.routes.cart_url.replace(/\/cart\/?$/, '') : '') || '/';
    const base = root.endsWith('/') ? root : root + '/';
    return `${base}?section_id=${SECTION_ID}`;
  }

  function updateCartIconBubble() {
    const cartIcon = document.querySelector(CART_ICON_SELECTOR);
    if (!cartIcon) return;

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    const sectionUrl = getSectionUrl();

    fetch(sectionUrl)
      .then((response) => {
        if (!response.ok) throw new Error('Section fetch failed');
        return response.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const sectionContent = doc.querySelector('[id*="cart-icon-bubble"]') || doc.querySelector('.shopify-section');
        if (sectionContent) {
          cartIcon.innerHTML = sectionContent.innerHTML;
          return;
        }
        throw new Error('No section content found');
      })
      .catch(() => {
        // Fallback: fetch cart JSON and update count manually
        fetch(`${cartUrl}.js`)
          .then((res) => res.json())
          .then((cart) => {
            const bubble = cartIcon.querySelector('.cart-count-bubble');
            const countSpan = bubble?.querySelector('span[aria-hidden="true"]');
            const screenReaderSpan = bubble?.querySelector('.visually-hidden');
            if (cart.item_count === 0) {
              if (bubble) bubble.remove();
            } else {
              if (!bubble) {
                const newBubble = document.createElement('div');
                newBubble.className = 'cart-count-bubble';
                const span = document.createElement('span');
                span.setAttribute('aria-hidden', 'true');
                span.textContent = cart.item_count < 100 ? String(cart.item_count) : '';
                newBubble.appendChild(span);
                const sr = document.createElement('span');
                sr.className = 'visually-hidden';
                sr.textContent = `${cart.item_count} items`;
                newBubble.appendChild(sr);
                cartIcon.appendChild(newBubble);
              } else if (countSpan) {
                countSpan.textContent = cart.item_count < 100 ? String(cart.item_count) : '';
              }
              if (screenReaderSpan) screenReaderSpan.textContent = `${cart.item_count} items`;
            }
          })
          .catch((err) => console.warn('AMP cart integration: failed to update cart icon', err));
      });
  }

  // Store any existing AMP callback so we can chain if needed
  const existingCartUpdated = window.AMP_CART_UPDATED;
  const existingCartLoaded = window.AMP_CART_LOADED;

  window.AMP_CART_UPDATED = function (rawCart, cartTarget) {
    updateCartIconBubble();
    openAmpCartForPendingAdd();
    if (typeof existingCartUpdated === 'function') {
      return existingCartUpdated(rawCart, cartTarget);
    }
  };

  window.AMP_CART_LOADED = function (cart) {
    updateCartIconBubble();
    openAmpCartForPendingAdd();
    if (typeof existingCartLoaded === 'function') {
      existingCartLoaded(cart);
    }
  };
})();
